import React, { useMemo, useRef, useEffect } from "react";
import { Box, TableContainer } from "@mui/material";
import { VariableSizeList } from "react-window";
import { IniInfo } from "system/types/interfaces";
import { SiteSubnetRow } from "./SiteSubnetRow";

interface SitesAndSubnetsTableProps {
  sitesAndSubnets: IniInfo[];
  onAdd: (index: number) => void;
  onDelete: (name: string) => void;
  onUndoDelete: (name: string) => void;
  onEdit: (item: IniInfo) => void;
  readOnlyViewer: boolean;
  searchTerm: string;
  searchResults: number[];
  currentResultIndex: number;
}

export const SitesAndSubnetsTable: React.FC<SitesAndSubnetsTableProps> = ({
  sitesAndSubnets,
  onAdd,
  onDelete,
  onUndoDelete,
  onEdit,
  readOnlyViewer,
  searchTerm,
  searchResults,
  currentResultIndex,
}) => {
  const listRef = useRef<VariableSizeList>(null);

  const filteredSitesAndSubnets = useMemo(() => {
    if (!searchTerm) return sitesAndSubnets;
    return sitesAndSubnets.filter(
      (item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.value.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sitesAndSubnets, searchTerm]);

  useEffect(() => {
    if (searchResults.length > 0 && currentResultIndex >= 0) {
      const scrollToIndex = searchResults[currentResultIndex];
      listRef.current?.scrollToItem(scrollToIndex, "center");
    }
  }, [currentResultIndex, searchResults]);

  const Row = React.memo(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = filteredSitesAndSubnets[index];
    return (
      <SiteSubnetRow
        key={item.id}
        item={item}
        index={index}
        onAdd={onAdd}
        onDelete={onDelete}
        onUndoDelete={onUndoDelete}
        onEdit={onEdit}
        readOnlyViewer={readOnlyViewer}
        searchTerm={searchTerm}
        style={style}
      />
    );
  });

  if (filteredSitesAndSubnets.length === 0) {
    return (
      <TableContainer sx={{ backgroundColor: "#1b1a19", borderRadius: 2, marginBottom: 3 }}>
        <Box sx={{ color: "#aaa", textAlign: "center", padding: 2 }}>
          {searchTerm ? "No sites and subnets match the search." : "No sites and subnets have been added yet."}
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer
      sx={{
        height: "auto",
        border: "1px solid #333",
        backgroundColor: "#1b1a19",
        borderRadius: "8px",
        overflow: "hidden",
      }}
      component="div"
    >
      {/* Header sticky */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "400px auto 250px",
          backgroundColor: "#333",
          color: "#fff",
          padding: "8px 16px",
          position: "sticky",
          top: 0,
          zIndex: 1,
          borderBottom: "1px solid #515151",
        }}
      >
        <Box sx={{ fontWeight: "bold" }}>Site Name</Box>
        <Box sx={{ fontWeight: "bold" }}>IP Ranges</Box>
        <Box sx={{ textAlign: "right", fontWeight: "bold" }}>Actions</Box>
      </Box>

      <VariableSizeList
        height={window.innerHeight}
        width="100%"
        itemCount={filteredSitesAndSubnets.length}
        itemSize={() => 70}
        ref={listRef}
      >
        {Row}
      </VariableSizeList>
    </TableContainer>
  );
};
